<template>
	<Card :title="candidate.user.full_name">
		<template #badges>
			<Badge :color="badgeColor" active>{{ trans(`rotations.elections.candidates.status.${candidate.status}`) }}</Badge>
		</template>
		<AcademicCertificateStatus v-if="candidate.is_pending" :key="`academic-certificate-user-${candidate.user.id}`" :certificate="candidate.user.last_academic_certificate"/>
		<CandidateProfile :candidate="candidate"/>
		<template #footer>
			<div v-if="candidate.is_pending" class="flex items-center gap-3">
				<PrimaryButton :color="Color.Green" @click.stop="accept">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.accept') }}
				</PrimaryButton>
				<SecondaryButton :color="Color.Red" @click.stop="reject">
					<i class="fa-solid fa-ban mr-2"/>
					{{ trans('general.reject') }}
				</SecondaryButton>
			</div>
			<SecondaryButton v-if="candidate.is_accepted && rotation.is_confirmed" :color="Color.Green" @click.stop="elect">
				<i class="fa-solid fa-vote-yea mr-2"/>
				{{ trans('rotations.elections.candidates.elect') }}
			</SecondaryButton>
			<a v-if="candidate.is_elected" :href="route('association.representatives.rotations.compromise-letters.create', {rotation: rotation.id, candidate: candidate.id})">
				<SecondaryButton :color="Color.Purple">
					<i class="fa-solid fa-download mr-2"/>
					{{ trans('rotations.elections.documentation.download') }}
				</SecondaryButton>
			</a>
			<a :href="route('association.representatives.rotations.candidates.certificate', {rotation: rotation.id, candidate: candidate.id})" target="_blank">
				<SoftButton :color="Color.Blue">
					<i class="fa-solid fa-certificate mr-2"/>
					{{ trans('admin.certifications.certificate') }}
				</SoftButton>
			</a>
		</template>
	</Card>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {ref, type Ref, defineProps, type ComputedRef, computed} from 'vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import CandidateProfile from '@/Components/Audit/Rotations/CandidateProfile.vue'
import {type RotationCandidate} from '@/Types/Periods/Rotations/RotationCandidate'
import {getRotationParticipantStatusColor} from '@/Helpers/Association/Representatives/participants'
import AcademicCertificateStatus from '@/Components/Certificates/Academic/AcademicCertificateStatus.vue'

const props = defineProps<{
	rotation:Rotation,
	candidate:RotationCandidate,
}>()

const working:Ref<boolean> = ref(false)
const rotation:Ref<Rotation> = ref(props.rotation)
const candidate:Ref<RotationCandidate> = ref(props.candidate)

const badgeColor:ComputedRef<Color> = computed(():Color => {
	return getRotationParticipantStatusColor(candidate.value)
})

function accept():void{
	if(!working.value && candidate.value.is_pending && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.candidates.update', {
			rotation: rotation.value.id,
			candidate: candidate.value.id
		}), {
			action: 'accept',
		}).then(({data}) => {
			candidate.value = data
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function reject():void{
	if(!working.value && candidate.value.is_pending && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.candidates.update', {
			rotation: rotation.value.id,
			candidate: candidate.value.id
		}), {
			action: 'reject',
		}).then(({data}) => {
			candidate.value = data
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function elect():void{
	if(!working.value && candidate.value.is_accepted && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.candidates.update', {
			rotation: rotation.value.id,
			candidate: candidate.value.id
		}), {
			action: 'elect',
		}).then(({data}) => {
			candidate.value = data
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>